import React from 'react';
import { useTheme } from '../context/ThemeContext';
import './AuthPages.scss';

const ReturnPolicyPage = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className={`auth-page ${isDark ? 'auth-page--dark' : 'auth-page--light'}`}>
      <div className="policy-page">
        <div className="policy-page__content">
          <h1 className="policy-page__title">Return Policy</h1>
          <p className="policy-page__updated"><strong>Last Updated:</strong> January 28, 2026</p>
          
          <p className="policy-page__intro">
            ChillVerify is committed to ensuring your complete satisfaction with our sensor products. This Return Policy outlines the procedures and conditions for returning products purchased from our website or authorized retailers.
          </p>

          <section className="policy-page__section">
            <h2>1. Return Eligibility Period</h2>
            <p>Products may be returned within the following timeframes:</p>
            <ul>
              <li><strong>30 days:</strong> All sensor products for a full refund or exchange</li>
              <li><strong>14 days:</strong> Digital products and software licenses</li>
              <li><strong>7 days:</strong> Defective products for expedited replacement</li>
              <li><strong>90 days:</strong> Warranty claims for manufacturing defects</li>
            </ul>
            <p>The return period begins from the date of delivery, not the purchase date.</p>
          </section>

          <section className="policy-page__section">
            <h2>2. Return Conditions</h2>
            <p>To be eligible for return, products must meet the following conditions:</p>
            
            <h3>2.1 Product Condition</h3>
            <ul>
              <li>Product must be in resalable condition</li>
              <li>No physical damage beyond normal inspection</li>
              <li>All components and accessories included</li>
              <li>No signs of excessive use or wear</li>
              <li>Serial numbers must be intact and readable</li>
            </ul>

            <h3>2.2 Packaging Requirements</h3>
            <ul>
              <li>Original packaging must be included</li>
              <li>Box must be undamaged and in good condition</li>
              <li>All manuals, cables, and accessories included</li>
              <li>Protective materials should be intact</li>
            </ul>

            <h3>2.3 Documentation Required</h3>
            <ul>
              <li>Original receipt or proof of purchase</li>
              <li>Order number or invoice</li>
              <li>Completed return form (provided by customer service)</li>
              <li>RMA (Return Merchandise Authorization) number</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>3. Non-Returnable Items</h2>
            <p>The following items cannot be returned:</p>
            <ul>
              <li>Products returned after the 30-day period</li>
              <li>Products damaged due to misuse or negligence</li>
              <li>Products without original packaging</li>
              <li>Customized or special-order products</li>
              <li>Consumable items (batteries, cables after use)</li>
              <li>Products with voided warranty seals</li>
              <li>Software that has been activated or extensively used</li>
              <li>Products purchased from unauthorized resellers</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>4. Return Process</h2>
            <p>Follow these steps to return your product:</p>
            
            <h3>Step 1: Contact Customer Service</h3>
            <ul>
              <li>Call: 1-800-CHILL-01 or Email: support@chillverify.com</li>
              <li>Provide order number and product details</li>
              <li>Explain reason for return</li>
              <li>Receive RMA number and return instructions</li>
            </ul>

            <h3>Step 2: Prepare the Return Package</h3>
            <ul>
              <li>Print and fill out the return form</li>
              <li>Include the RMA number on the package</li>
              <li>Package the product securely with all accessories</li>
              <li>Use original packaging if possible</li>
              <li>Remove any personal data or accounts</li>
            </ul>

            <h3>Step 3: Ship the Return</h3>
            <ul>
              <li>Use the provided return shipping label (if applicable)</li>
              <li>Ship to the address provided with your RMA</li>
              <li>Use a trackable shipping method</li>
              <li>Keep your tracking number for reference</li>
              <li>Insure the package for the full product value</li>
            </ul>

            <h3>Step 4: Return Processing</h3>
            <ul>
              <li>Allow 3-5 business days for delivery to our facility</li>
              <li>Inspection takes 2-3 business days upon receipt</li>
              <li>You'll receive email confirmation of processing</li>
              <li>Refund or exchange will be processed within 5-10 days</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>5. Return Shipping Options</h2>
            
            <h3>5.1 ChillVerify-Provided Labels</h3>
            <ul>
              <li>Free for defective products</li>
              <li>Free for errors made by ChillVerify</li>
              <li>$9.99 fee for non-defective returns (deducted from refund)</li>
            </ul>

            <h3>5.2 Customer-Arranged Shipping</h3>
            <ul>
              <li>Customer pays shipping costs for non-defective returns</li>
              <li>Must use trackable shipping method</li>
              <li>Insurance recommended for high-value items</li>
              <li>ChillVerify not responsible for lost packages</li>
            </ul>

            <h3>5.3 International Returns</h3>
            <ul>
              <li>Customer responsible for all shipping costs</li>
              <li>Customs and duties may apply</li>
              <li>Declare as "Returned Merchandise" on customs forms</li>
              <li>Processing time may be longer (7-14 days)</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>6. Inspection and Processing</h2>
            <p>Upon receipt, all returns undergo inspection:</p>
            
            <h3>6.1 Inspection Criteria</h3>
            <ul>
              <li>Product condition and functionality</li>
              <li>Completeness of accessories and packaging</li>
              <li>Verification of serial numbers</li>
              <li>Check for water damage or physical abuse</li>
              <li>Software and firmware status</li>
            </ul>

            <h3>6.2 Processing Outcomes</h3>
            <ul>
              <li><strong>Approved:</strong> Full refund or exchange processed</li>
              <li><strong>Partial Approval:</strong> Partial refund if conditions not fully met</li>
              <li><strong>Rejected:</strong> Product returned to customer (shipping charges apply)</li>
              <li><strong>Warranty Claim:</strong> Directed to warranty department</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>7. Exchange Process</h2>
            <p>Exchanges follow the same process as returns with these additional considerations:</p>
            
            <h3>7.1 Exchange Options</h3>
            <ul>
              <li>Same product, different color</li>
              <li>Different model (price difference applies)</li>
              <li>Upgraded version (pay difference)</li>
              <li>Downgraded version (receive difference)</li>
            </ul>

            <h3>7.2 Exchange Timeline</h3>
            <ul>
              <li>Original return processed first (2-3 days)</li>
              <li>Exchange item ships within 1-2 business days</li>
              <li>Total time: 7-14 business days</li>
              <li>Express exchange available for $19.99</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>8. Restocking Fees</h2>
            <p>Restocking fees may apply in certain situations:</p>
            <ul>
              <li><strong>15% fee:</strong> Products with damaged packaging</li>
              <li><strong>15% fee:</strong> Missing accessories or manuals</li>
              <li><strong>10% fee:</strong> Products showing excessive use</li>
              <li><strong>0% fee:</strong> Defective products or ChillVerify errors</li>
            </ul>
            <p>Restocking fees are deducted from the refund amount.</p>
          </section>

          <section className="policy-page__section">
            <h2>9. Damaged or Defective Returns</h2>
            
            <h3>9.1 Upon Arrival</h3>
            <p>If your product arrives damaged:</p>
            <ul>
              <li>Report within 48 hours of delivery</li>
              <li>Provide photos of damage and packaging</li>
              <li>Keep all packaging materials</li>
              <li>Free replacement or full refund offered</li>
            </ul>

            <h3>9.2 During Use</h3>
            <p>If product becomes defective during use:</p>
            <ul>
              <li>Check warranty period (typically 1-2 years)</li>
              <li>Contact support for troubleshooting</li>
              <li>Warranty claims processed separately</li>
              <li>Repair or replacement options available</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>10. Return Status Tracking</h2>
            <p>Track your return status through:</p>
            <ul>
              <li><strong>Email Updates:</strong> Automatic notifications at each step</li>
              <li><strong>Online Portal:</strong> Track at chillverify.com/returns</li>
              <li><strong>Customer Service:</strong> Call with your RMA number</li>
              <li><strong>Status Updates:</strong> Received, Inspecting, Approved, Processed</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>11. Return Address</h2>
            <p>Send all returns to:</p>
            <div style={{ 
              backgroundColor: isDark ? '#262626' : '#f4f4f4', 
              padding: '16px', 
              borderRadius: '8px', 
              margin: '16px 0',
              border: `1px solid ${isDark ? '#393939' : '#e0e0e0'}`
            }}>
              <p><strong>ChillVerify Returns Department</strong><br />
              8 Beachrock View<br />
              East Devonport, Tasmania 7310<br />
              Australia<br />
              <strong>Attn:</strong> RMA #[Your RMA Number]</p>
            </div>
            <p><strong>Important:</strong> Always include your RMA number on the package.</p>
          </section>

          <section className="policy-page__section">
            <h2>12. Return Policy Exceptions</h2>
            <p>Special circumstances may warrant exceptions:</p>
            <ul>
              <li><strong>Medical Devices:</strong> Extended return periods for medical monitoring needs</li>
              <li><strong>Bulk Orders:</strong> Custom return terms for orders over 10 units</li>
              <li><strong>Educational Institutions:</strong> Special return policies for schools</li>
              <li><strong>Government Contracts:</strong> Contract-specific return terms</li>
            </ul>
            <p>Contact customer service for special circumstances.</p>
          </section>

          <section className="policy-page__section">
            <h2>13. Customer Support</h2>
            <p>For return assistance, contact us:</p>
            <ul>
              <li><strong>Phone:</strong> +61 3 6427 1234</li>
              <li><strong>Email:</strong> returns@chillverify.com</li>
              <li><strong>Live Chat:</strong> Available on our website</li>
              <li><strong>Hours:</strong> Monday-Friday, 9:00 AM - 5:00 PM AEST</li>
              <li><strong>Response Time:</strong> Within 24 hours for emails</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>14. Policy Updates</h2>
            <p>
              ChillVerify may update this Return Policy periodically. Changes will be posted on our website and become effective immediately. Your continued use of our products constitutes acceptance of any policy modifications.
            </p>
          </section>

          <div className="policy-page__footer">
            <p>
              This Return Policy is designed to ensure fair and transparent return procedures while protecting both our customers and our business. Thank you for choosing ChillVerify.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnPolicyPage;
